<template>
    <OnboardingLayout v-bind="appSharedData.getOnboardingLayoutProps()">
        <template #content>
            <OnboardingStepContainer
                :showStepsIndicator="true"
                :stepNumber="7"
            >
                <template #title>Your booking page is live!</template>
                <template #subtitle>
                    <p>
                        You can share your link now. Next, preview your page and
                        book a quick test meeting.
                    </p>
                </template>
                <template #content>
                    <div class="max-w-4xl mx-auto block w-full">
                        <div class="space-y-4">
                            <div class="grid grid-cols-12 gap-4">
                                <div class="col-span-12 md:col-span-6">
                                    <Card>
                                        <template #title>
                                            <span class="text-gray-500 text-sm">
                                                Live booking URL
                                            </span>
                                        </template>
                                        <template #content>
                                            <a
                                                class="text-xl font-bold text-primary hover:underline"
                                                :href="bookingPageUrl"
                                                target="_blank"
                                            >
                                                {{
                                                    bookingPageUrl
                                                        .replace('https://', '')
                                                        .replace('http://', '')
                                                }}
                                            </a>
                                        </template>
                                    </Card>
                                </div>
                                <div class="col-span-12 md:col-span-6">
                                    <Card>
                                        <template #title>
                                            <span class="text-gray-500 text-sm">
                                                First service
                                            </span>
                                        </template>
                                        <template #content>
                                            <div
                                                class="text-xl font-bold text-gray-900"
                                            >
                                                {{ localFirstService.title }}
                                            </div>
                                            <div
                                                class="text-sm font-base text-gray-500"
                                            >
                                                {{
                                                    localFirstService.description
                                                }}
                                            </div>
                                        </template>
                                    </Card>
                                </div>
                            </div>
                            <div>
                                <Card>
                                    <template #title>
                                        <CardTitle title="Next steps" />
                                    </template>
                                    <template #content>
                                        <div
                                            class="grid grid-cols-12 gap-x-4 gap-y-2"
                                        >
                                            <div
                                                class="col-span-12 md:col-span-6 px-4 py-2 bg-stone-50 border border-stone-100 rounded-lg text-sm font-medium"
                                            >
                                                Preview your page
                                            </div>
                                            <div
                                                class="col-span-12 md:col-span-6 px-4 py-2 bg-stone-50 border border-stone-100 rounded-lg text-sm font-medium"
                                            >
                                                Book a test meeting
                                            </div>
                                            <div
                                                class="col-span-12 md:col-span-6 px-4 py-2 bg-stone-50 border border-stone-100 rounded-lg text-sm font-medium"
                                            >
                                                Connect your calendar
                                            </div>
                                            <div
                                                class="col-span-12 md:col-span-6 px-4 py-2 bg-stone-50 border border-stone-100 rounded-lg text-sm font-medium"
                                            >
                                                Add your logo
                                            </div>
                                            <div
                                                class="col-span-12 md:col-span-6 px-4 py-2 bg-stone-50 border border-stone-100 rounded-lg text-sm font-medium"
                                            >
                                                Share your link
                                            </div>
                                        </div>
                                    </template>
                                </Card>
                            </div>
                        </div>
                    </div>
                </template>
                <template #action></template>
            </OnboardingStepContainer>
        </template>

        <template #footerRight>
            <ApiForm
                method="POST"
                :url="RoutePathAppEnum.Onboarding_Step7_Finished"
                ref="apiFormRef"
            >
                <Button
                    @click="apiFormRef?.submit()"
                    iconName="arrow-right"
                    color="primary"
                    :iconRight="true"
                    :loading="apiFormRef?.loading.valueOf()"
                >
                    Go to my Dashboard
                </Button>
            </ApiForm>
        </template>
    </OnboardingLayout>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import OnboardingLayout from '@/Shared/Layout/Onboarding/OnboardingLayout.vue'
    import Button from '@/Shared/Component/Button.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import OnboardingStepContainer from '@/App/Company/Component/OnboardingStepContainer.vue'
    import Service from '@/App/Service/Entity/Service'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import {
        RoutePathAppEnum,
        RoutePathBookingFormEnum,
    } from '@/Shared/AutoGenerated/RoutePathEnum'
    import type { ServiceData } from '@/Shared/AutoGenerated/Main'
    import Card from '@/Shared/Component/Card.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import CardTitle from '@/Shared/Component/CardTitle.vue'

    interface ShowOnboardingStepFinishedProps {
        appSharedDataData: any
        firstServiceData: ServiceData
    }

    const props = defineProps<ShowOnboardingStepFinishedProps>()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const apiFormRef = ref<ApiFormRefType>(null)
    const localFirstService = Service.hydrateSingle(props.firstServiceData)
    const authCompany = appSharedData.getAuthCompany()

    const bookingPageUrl = new UrlFactory().makeCompanyBookingFormUrl(
        authCompany.slug,
    )
</script>
